import { useEffect, useRef, useState, type RefObject } from 'react';
import { diag } from '../../core/diag';
import type { AppError } from '../../core/types';
import { OneFlowError } from '../../core/errors';
import { mediaSourceCtor } from '../../engine/capabilities';
import { engine, useApp } from '../../state/store';
import type { Mp4Info, PlayerPhase } from '../../media/mp4-player';
import { FilePlayer, ProgressiveAudioPlayer, estimateMp3Seconds } from '../../media/native-player';

export type Plan =
  'sniffing' | 'mp4' | 'progressive' | 'file' | 'wait-complete' | 'unsupported-kind';

export interface PipelineState {
  plan: Plan;
  phase: PlayerPhase | 'idle';
  info: Mp4Info | null;
  waiting: string | null;
  error: AppError | null;
  /** Estimated duration for sources the parser cannot index. */
  durationHint: number | null;
  /** True when random seeking is not yet possible. */
  limitedSeek: boolean;
}

const MP4_FAMILY = new Set(['mp4', 'mov', 'm4a']);
const PROGRESSIVE: Record<string, string> = { mp3: 'audio/mpeg', aac: 'audio/aac' };

export function decidePlan(
  container: string | null,
  complete: boolean,
  /** True when streaming through MediaSource is impossible or already failed for this file. */
  noStreaming = false,
): Plan {
  if (container === null) return 'sniffing';
  if (MP4_FAMILY.has(container)) {
    if (noStreaming) return complete ? 'file' : 'wait-complete';
    return 'mp4';
  }
  const prog = PROGRESSIVE[container];
  if (prog) {
    if (complete) return 'file';
    return ProgressiveAudioPlayer.supported(prog) ? 'progressive' : 'wait-complete';
  }
  if (['webm', 'ogg', 'wav', 'flac', 'mkv', 'avi'].includes(container))
    return complete ? 'file' : 'wait-complete';
  return 'unsupported-kind';
}

/**
 * Attaches the right local playback path to a media element. The element only
 * ever receives local object URLs; the remote URL never reaches it. ADR-001, ALG-041
 */
export function useMediaPipeline(
  id: string,
  mediaRef: RefObject<HTMLMediaElement | null>,
): PipelineState {
  const container = useApp((s) => s.records[id]?.container ?? null);
  const complete = useApp((s) => s.records[id]?.state === 'complete');
  const [st, setSt] = useState<PipelineState>({
    plan: 'sniffing',
    phase: 'idle',
    info: null,
    waiting: null,
    error: null,
    durationHint: null,
    limitedSeek: false,
  });
  // Without MSE, or when MSE cannot decode this file, the finished file may still play natively.
  const [streamFailed, setStreamFailed] = useState(false);
  const noStreaming = !mediaSourceCtor() || streamFailed;
  const lastTime = useRef(0);
  /** Whether playback was running when the previous player was torn down. */
  const wasPlaying = useRef(false);
  const plan = decidePlan(container, complete, noStreaming);

  useEffect(() => setStreamFailed(false), [id]);

  useEffect(() => {
    const media = mediaRef.current;
    const handle = engine.handle(id);
    setSt((s) => ({
      ...s,
      plan,
      phase: 'idle',
      error: null,
      waiting: null,
      info: plan === s.plan ? s.info : null,
    }));
    if (!media || !handle) return;
    if (plan === 'sniffing' || plan === 'wait-complete' || plan === 'unsupported-kind') return;

    const rec = engine.getRecord(id);
    const startAt = lastTime.current > 0 ? lastTime.current : (rec?.position ?? 0);
    let disposed = false;
    let dispose: (() => void) | null = null;
    const owner = `media:${id}`;

    const onTime = () => {
      lastTime.current = media.currentTime;
    };
    media.addEventListener('timeupdate', onTime);
    const savePos = setInterval(() => {
      if (!media.paused && media.currentTime > 1) engine.savePosition(id, media.currentTime);
    }, 5000);

    const fail = (error: AppError, unsupported = false) => {
      if (!disposed) setSt((s) => ({ ...s, phase: unsupported ? 'unsupported' : 'error', error }));
    };

    if (plan === 'mp4') {
      // mp4box is large; fetch it only when an MP4 is actually played.
      let limitTimer: ReturnType<typeof setInterval> | null = null;
      let player: { dispose(): void } | null = null;
      void import('../../media/mp4-player').then(({ Mp4Player }) => {
        if (disposed) return;
        const p = new Mp4Player(
          handle,
          media,
          {
            onPhase: (phase, detail) => {
              if (disposed) return;
              if (phase === 'unsupported') {
                diag.log(
                  'warn',
                  'media',
                  'MediaSource could not play this file; will try the finished file',
                );
                setStreamFailed(true);
                return;
              }
              setSt((s) => ({ ...s, phase, error: detail?.error ?? null }));
            },
            onInfo: (info) => !disposed && setSt((s) => ({ ...s, info })),
            onWaiting: (waiting) => !disposed && setSt((s) => ({ ...s, waiting })),
            onFeed: (offset) => !disposed && useApp.getState().setFeed(id, offset),
          },
          { owner, constrained: engine.caps.constrained, startAt },
        );
        player = p;
        void p.start();
        limitTimer = setInterval(() => {
          const l = p.seekLimit();
          setSt((s) =>
            s.limitedSeek === (l !== Infinity) ? s : { ...s, limitedSeek: l !== Infinity },
          );
        }, 1000);
      });
      dispose = () => {
        if (limitTimer) clearInterval(limitTimer);
        player?.dispose();
      };
    } else if (plan === 'progressive' && container) {
      const mime = PROGRESSIVE[container]!;
      const player = new ProgressiveAudioPlayer(
        handle,
        media as HTMLAudioElement,
        owner,
        {
          onError: (e) => fail(e),
          onWaiting: (w) => !disposed && setSt((s) => ({ ...s, waiting: w })),
        },
        mime,
      );
      dispose = () => player.dispose();
      setSt((s) => ({ ...s, phase: 'ready', limitedSeek: true }));
      void player
        .start()
        .catch((e) =>
          fail(
            e instanceof OneFlowError ? e.appError : new OneFlowError('PREVIEW_FAILED').appError,
          ),
        );
      void handle.readAt(0, 4096).then((r) => {
        if (r.status === 'ok' && handle.total) {
          const secs = estimateMp3Seconds(r.bytes, handle.total);
          if (secs && !disposed) setSt((s) => ({ ...s, durationHint: secs }));
        }
      });
    } else if (plan === 'file') {
      const player = new FilePlayer(engine, id, media, owner);
      dispose = () => player.dispose();
      setSt((s) => ({ ...s, phase: 'indexing', limitedSeek: false }));
      player
        .start(startAt, wasPlaying.current)
        .then(() => !disposed && setSt((s) => ({ ...s, phase: 'ready' })))
        .catch((e) =>
          fail(
            e instanceof OneFlowError ? e.appError : new OneFlowError('PREVIEW_FAILED').appError,
          ),
        );
      const onErr = () => {
        const code = media.error?.code;
        fail(
          new OneFlowError(code === 3 || code === 4 ? 'DECODE_UNSUPPORTED' : 'PREVIEW_FAILED')
            .appError,
          code === 3 || code === 4,
        );
      };
      media.addEventListener('error', onErr);
      const prev = dispose;
      dispose = () => {
        media.removeEventListener('error', onErr);
        prev();
      };
    }

    return () => {
      disposed = true;
      clearInterval(savePos);
      useApp.getState().setFeed(id, null);
      media.removeEventListener('timeupdate', onTime);
      if (media.currentTime > 1) engine.savePosition(id, media.currentTime);
      wasPlaying.current = !media.paused && !media.ended;
      dispose?.();
    };
  }, [id, plan, container, mediaRef]);

  return st;
}
